/**
 * Admin Portal → Partners → a partner → Contacts → Review queue (partner webhook intake,
 * workstream S5).
 *
 * The people waiting to be sent their invitation: the contacts with `mail_state=imported` in the
 * scope (a tag, an import). A review-mode tag holds everyone here; even on an automatic tag, CSV
 * uploads and the people who were waiting before the switch stay here. Each person opens THEIR drafted
 * email — rendered by the mailer itself with their own details (`GET …/contacts/{cid}/draft`, S2;
 * nothing is sent) — in a sandboxed preview, and Send there goes to that one person
 * (`{contact_ids: [cid]}` on `POST …/contacts/send` — no tag or import, so a server that doesn't know
 * `contact_ids` refuses it rather than sending the whole tag). It says "Queued" only when the answer
 * shows it; a refusal (another
 * partner already invited them, …) says why and leaves the draft open. Sending everyone waiting on
 * a tag stays the Send card below (a test to your own address first, then the count typed).
 *
 * Each person shows why they wait — what a Send would answer for them right now (S1 #5418, admin
 * only: a verdict says whether an address holds an account). A person a Send would skip (already
 * has an account, already invited, suppressed, …) can have their draft read, but their Send is
 * held back here, and the screen says why.
 *
 * Addresses are masked; a paused or ended tag's drafts can be read but not sent.
 *
 * Web-only (the admin SPA).
 */

import React, { useEffect, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import { type PartnerStatus, type TagRow } from './partnersLogic';
import {
  contactEventText,
  contactName,
  maskEmail,
  sendBlockedByVerdict,
  sendBlockedReason,
  sendOneResult,
  sendVerdictLabel,
  type ContactRow,
} from './partnerContactsLogic';
import { previewDocument } from './websiteCode';
import { NoticeLine, errText, fmtDate, hint, row, td, th, useBusy, type Notice } from './PartnersShared';

export const REVIEW_PAGE_SIZE = 50;

const lowerFirst = (t: string) => (t ? t.charAt(0).toLowerCase() + t.slice(1) : t);

interface Draft {
  contact: ContactRow;
  subject: string;
  html: string;
  text: string;
}

export const PartnerReviewQueue: React.FC<{
  partnerId: string;
  tags: TagRow[];
  partnerStatus: PartnerStatus;
  /** The Contacts tab's scope; none = every tag. */
  tagId?: string;
  importId?: string;
  /** Bumped by the tab when contacts change elsewhere (an import, a send). */
  reloadKey: number;
  /** After a person is sent their invitation: the tab re-reads its funnel and list. */
  onSent: () => void;
}> = ({ partnerId, tags, partnerStatus, tagId, importId, reloadKey, onSent }) => {
  const [waiting, setWaiting] = useState<ContactRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [own, setOwn] = useState(0);
  const [busy, run] = useBusy();

  useEffect(() => {
    let live = true;
    PartnersAdmin.listContacts(partnerId, { tagId, importId, mailState: 'imported', limit: REVIEW_PAGE_SIZE, offset: 0 })
      .then((res) => { if (live) { setWaiting(res.contacts || []); setTotal(Number(res.total || 0)); } })
      .catch((e) => { if (live) { setWaiting([]); setTotal(null); setNotice({ kind: 'error', text: errText(e) }); } });
    return () => { live = false; };
  }, [partnerId, tagId, importId, reloadKey, own]);

  const tagOf = (id: string) => tags.find((t) => t.id === id) ?? null;

  const open = (c: ContactRow) =>
    run(async () => {
      try {
        const res = await PartnersAdmin.contactDraft(partnerId, c.id);
        setDraft({ contact: c, subject: res.subject, html: res.html, text: res.text });
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const sendOne = () =>
    run(async () => {
      if (!draft) return;
      const who = contactName(draft.contact);
      try {
        // "Queued" only when the answer shows it; a refusal keeps the draft open and says why.
        const out = sendOneResult(await PartnersAdmin.sendOneContact(partnerId, draft.contact.id), who);
        if (out.sent === false) {
          setNotice({ kind: 'error', text: out.text });
          return;
        }
        setNotice({ kind: out.sent ? 'ok' : 'info', text: out.text });
        setDraft(null);
        setOwn((k) => k + 1);
        onSent();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const draftTag = draft ? tagOf(draft.contact.tag_id) : null;
  const blocked = draftTag ? sendBlockedReason(draftTag.status, partnerStatus) : sendBlockedReason('active', partnerStatus);
  // The Send's own verdict for this person: a refusal holds the button back (the server would skip them).
  const verdictHold = draft ? sendBlockedByVerdict(draft.contact.send_verdict) : null;

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
        {/* "waiting", not "waiting to send": a Send would skip some of them, and each row says why. */}
        {total == null ? 'Review queue' : total === 0 ? 'Review queue' : `${total.toLocaleString('en-CA')} waiting`}
      </h4>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      {total === 0 && <div style={hint}>Nobody is waiting here.</div>}
      {waiting.length > 0 && (
        <>
          <div style={hint}>
            Open a person to read the exact email they will get. To send everyone waiting on a tag at once, use Send below.
          </div>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Name</th><th style={th}>Email</th><th style={th}>Event</th><th style={th}>Tag</th><th style={th}>Arrived</th>
                  <th style={th}>Why waiting</th><th style={th} />
                </tr>
              </thead>
              <tbody>
                {waiting.map((c) => (
                  <tr key={c.id} style={{ background: draft?.contact.id === c.id ? 'var(--surface-subtle, #f9fafb)' : undefined }}>
                    <td style={td}>{contactName(c)}</td>
                    <td style={td}><code>{maskEmail(c.email_masked)}</code></td>
                    <td style={td}>{contactEventText(c)}</td>
                    <td style={td}>{c.tag}</td>
                    <td style={td}>{fmtDate(c.created_at)}</td>
                    <td style={td}>{c.send_verdict ? sendVerdictLabel(c.send_verdict) : '—'}</td>
                    <td style={td}><AdminButton size="sm" variant="ghost" onClick={() => open(c)} disabled={busy}>Open draft</AdminButton></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total != null && total > waiting.length && (
            <div style={hint}>{`Showing the newest ${waiting.length} of ${total.toLocaleString('en-CA')}.`}</div>
          )}
        </>
      )}

      {draft && (
        <div data-testid="contact-draft" style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid var(--border-strong, #d1d5db)' }}>
          <div style={{ ...row, justifyContent: 'space-between', alignItems: 'center' }}>
            <b style={{ fontSize: 13 }}>{`Draft for ${contactName(draft.contact)} · ${maskEmail(draft.contact.email_masked)}`}</b>
            <AdminButton size="sm" variant="ghost" onClick={() => setDraft(null)}>Close</AdminButton>
          </div>
          <div style={{ fontSize: 13, margin: '8px 0' }}>Subject: <b>{draft.subject}</b></div>
          <iframe
            title={`Drafted email for ${contactName(draft.contact)}`}
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={previewDocument(draft.html)}
            style={{ width: '100%', height: 320, border: '1px dashed var(--border-strong, #d1d5db)', borderRadius: 8, background: '#ffffff' }}
          />
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 12, cursor: 'pointer' }}>Plain-text version</summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{draft.text}</pre>
          </details>
          <div style={{ ...row, marginTop: 8, alignItems: 'center' }}>
            {blocked || verdictHold
              ? (
                <span style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>
                  {blocked ?? `Not sent from here: ${lowerFirst(verdictHold!)}.`}
                </span>
              )
              : <AdminButton size="sm" onClick={sendOne} disabled={busy}>{`Send to ${contactName(draft.contact)}`}</AdminButton>}
            <span style={hint}>Preview only — links are turned off here. Nothing is sent until you press Send.</span>
          </div>
        </div>
      )}
    </AdminCard>
  );
};

export default PartnerReviewQueue;
